import Alert from '@components/atoms/Alert';
import { Button } from '@components/atoms/Button';
import { Card } from '@components/atoms/Card';
import { Input } from '@components/atoms/Input';
import ReactSelect from '@components/atoms/ReactSelect';
import { TextareaInput } from '@components/atoms/TextareaInput';
import { DataTable } from '@components/molecules/DataTable';
import { DataTableColumn } from '@components/molecules/DataTable/DataTable.types';
import PageHeader from '@components/molecules/PageHeader';
import { StatusComponentRow } from '@components/molecules/StatusComponentRow';
import {
  useClearPlatformStatusMessage,
  useClearPlatformStatusOverride,
  usePlatformStatusDetail,
  usePublishPlatformStatusMessage,
  useSetPlatformStatusOverride,
} from '@hooks/api/platformStatus/usePlatformStatusAdmin';
import {
  PlatformClientMetric,
  PlatformComponentKey,
  PlatformComponentState,
  PlatformDependencyDetail,
  PlatformStatusSeverity,
} from '@models/platformStatus.model';
import { formatRelativeTime, STATE_PRESENTATION } from '@utils/platformStatus';
import React, { useState } from 'react';

/**
 * Four hours.
 *
 * Every override is temporary by default because nothing else in this feature
 * will ever remind anyone to revisit it — there is no incident history and no
 * alerting. Left indefinite, a component set to MAINTENANCE during a Tuesday
 * deploy is still telling customers so in March. Expiring by default makes the
 * lazy outcome "returns to the truth" instead of "stays whatever was typed".
 */
const DEFAULT_OVERRIDE_HOURS = 4;

const toLocalInputValue = (date: Date): string => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const defaultExpiry = (): string => {
  const expiry = new Date();
  expiry.setHours(expiry.getHours() + DEFAULT_OVERRIDE_HOURS);
  return toLocalInputValue(expiry);
};

const COMPONENT_OPTIONS = Object.values(PlatformComponentKey).map((key) => ({
  value: key,
  label: key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' '),
}));

const STATE_OPTIONS = Object.values(PlatformComponentState)
  .filter((state) => state !== PlatformComponentState.UNKNOWN)
  .map((state) => ({ value: state, label: STATE_PRESENTATION[state].label }));

const SEVERITY_OPTIONS = Object.values(PlatformStatusSeverity).map((severity) => ({
  value: severity,
  label: severity.charAt(0) + severity.slice(1).toLowerCase(),
}));

const dependencyColumns: DataTableColumn<PlatformDependencyDetail>[] = [
  { key: 'label', header: 'Dependency', render: (row) => row.label },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <span
        className={
          row.status === 'up'
            ? 'text-green-700 dark:text-green-400'
            : row.status === 'unconfigured'
              ? 'text-gray-500'
              : 'text-red-700 dark:text-red-400'
        }>
        {row.status}
      </span>
    ),
  },
  { key: 'latencyMs', header: 'Latency', align: 'right', render: (row) => `${row.latencyMs} ms` },
  {
    key: 'error',
    header: 'Last error',
    hideOnSmall: true,
    render: (row) => (
      <span className="text-xs text-gray-500 dark:text-gray-400">{row.error ?? '—'}</span>
    ),
  },
];

const metricColumns: DataTableColumn<PlatformClientMetric>[] = [
  { key: 'client', header: 'Client', render: (row) => row.client },
  { key: 'total', header: 'Operations', align: 'right', render: (row) => row.total },
  { key: 'errors', header: 'Errors', align: 'right', render: (row) => row.errors },
  {
    key: 'errorRate',
    header: 'Error rate',
    align: 'right',
    // Null is not zero. Below the backend's minimum volume floor there is not
    // enough traffic for a percentage to mean anything, and printing "0%" would
    // read as a clean bill of health we have not actually earned.
    render: (row) =>
      row.errorRate === null || row.errorRate === undefined
        ? <span className="text-gray-400">not enough traffic</span>
        : `${(row.errorRate * 100).toFixed(1)}%`,
  },
];

const PlatformStatusAdminPage: React.FC = () => {
  const { data, isLoading } = usePlatformStatusDetail();

  const setOverride = useSetPlatformStatusOverride();
  const clearOverride = useClearPlatformStatusOverride();
  const publishMessage = usePublishPlatformStatusMessage();
  const clearMessage = useClearPlatformStatusMessage();

  const [componentKey, setComponentKey] = useState<PlatformComponentKey>(
    PlatformComponentKey.API,
  );
  const [state, setState] = useState<PlatformComponentState>(
    PlatformComponentState.DEGRADED,
  );
  const [publicMessage, setPublicMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [expiresAt, setExpiresAt] = useState(defaultExpiry);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<PlatformStatusSeverity>(
    PlatformStatusSeverity.INCIDENT,
  );

  const submitOverride = () => {
    setOverride.mutate({
      componentKey,
      state,
      publicMessage: publicMessage || undefined,
      internalNote: internalNote || undefined,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
    });
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Platform Status"
        subtitle={
          data
            ? `Last sampled ${formatRelativeTime(data.sampledAt)}`
            : 'Loading current status…'
        }
      />

      <Alert variant="info" showIcon title="Anything published here is world-readable">
        The public page at{' '}
        <span className="font-mono">getzenhammer.com/status</span> needs no login.
        Component messages and the banner are visible to anyone on the internet.
      </Alert>

      <Card>
        <h2 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          What the public sees right now
        </h2>
        {data?.components.map((component) => (
          <StatusComponentRow key={component.key} component={component} />
        ))}
      </Card>

      <Card>
        <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
          Override a component
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">
              Component
            </label>
            <ReactSelect
              options={COMPONENT_OPTIONS}
              value={COMPONENT_OPTIONS.find((option) => option.value === componentKey)}
              onChange={(option: any) => setComponentKey(option.value)}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">
              State
            </label>
            <ReactSelect
              options={STATE_OPTIONS}
              value={STATE_OPTIONS.find((option) => option.value === state)}
              onChange={(option: any) => setState(option.value)}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">
              Expires at
            </label>
            <Input
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Defaults to {DEFAULT_OVERRIDE_HOURS} hours. When it expires the
              component returns to automatic checks on its own.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">
              Public message (optional)
            </label>
            <TextareaInput
              rows={3}
              value={publicMessage}
              placeholder="Shown to customers beside this component."
              onChange={(event) => setPublicMessage(event.target.value)}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">
              Internal note (never shown publicly)
            </label>
            <TextareaInput
              rows={2}
              value={internalNote}
              placeholder="Context for whoever picks this up next."
              onChange={(event) => setInternalNote(event.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={submitOverride} disabled={setOverride.isPending}>
            Publish override
          </Button>
          <Button
            variant="secondary"
            onClick={() => clearOverride.mutate(componentKey)}
            disabled={clearOverride.isPending}>
            Back to automatic
          </Button>
        </div>

        {!!data?.overrides.length && (
          <div className="mt-4 text-sm text-gray-600 dark:text-gray-300">
            <p className="font-medium">Active overrides</p>
            <ul className="mt-1 list-inside list-disc">
              {data.overrides.map((override) => (
                <li key={override.componentKey}>
                  {override.componentKey} — {STATE_PRESENTATION[override.state].label}
                  {override.expiresAt
                    ? ` (until ${new Date(override.expiresAt).toLocaleString()})`
                    : ' (no expiry — will stay until cleared by hand)'}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
          Banner message
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">
              Title
            </label>
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">
              Severity
            </label>
            <ReactSelect
              options={SEVERITY_OPTIONS}
              value={SEVERITY_OPTIONS.find((option) => option.value === severity)}
              onChange={(option: any) => setSeverity(option.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-300">
              Body
            </label>
            <TextareaInput
              rows={3}
              value={body}
              placeholder="What we know, what we are doing, when we will say more."
              onChange={(event) => setBody(event.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={() => publishMessage.mutate({ title, body, severity })}
            disabled={!title || !body || publishMessage.isPending}>
            Publish message
          </Button>
          <Button
            variant="secondary"
            onClick={() => clearMessage.mutate()}
            disabled={clearMessage.isPending}>
            Remove message
          </Button>
        </div>
      </Card>

      <Card>
        <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
          Dependency checks
        </h2>
        <DataTable
          columns={dependencyColumns}
          rows={data?.dependencies ?? []}
          rowKey={(row) => row.label}
          isLoading={isLoading}
          caption="Dependency checks"
        />
      </Card>

      <Card>
        <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
          Client error rates
        </h2>
        <DataTable
          columns={metricColumns}
          rows={data?.metrics ?? []}
          rowKey={(row) => row.client}
          isLoading={isLoading}
          caption="Client error rates"
        />
      </Card>
    </div>
  );
};

export default PlatformStatusAdminPage;
